import styled from 'styled-components';

export const TrainingPlannerContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
  padding-top: 2.2rem;
`;

export const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 2rem;
`;

export const ButtonWrapper = styled.div`
  display: inline-block;
  margin-top: 0;
  margin-bottom: 2rem;
`;

export const PageSubtitle = styled.p`
  font-size: 1rem;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
  margin-top: 0;
  margin-bottom: 1.2rem;
  text-align: left;
`;

export const AddButton = styled.button`
  margin-top: 0.7rem;
  padding: 0.4rem 1rem;
  border-radius: 0.5rem;
  border: 2px solid var(--accent-orange);
  background: var(--accent-orange);
  color: #fff;
  font-family: 'Poppins', sans-serif;
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: -0.02em;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
  width: auto;
  display: inline-block;
  white-space: nowrap;

  &:hover, &:focus {
    background: var(--accent-orange-dark);
    border-color: var(--accent-orange-dark);
    color: #fff;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #ccc;
    border-color: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

/** Wrapper for plan-new-session form – bright grey background, matches /training-sessions form design */
export const FormAreaWrapper = styled.div`
  background: var(--bg-sidebar);
  border-radius: 1rem;
  padding: 1.5rem;
  margin-bottom: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 0.75rem;
  }
`;

export const FormSection = styled.section`
  margin-bottom: 0;
`;

export const FormTitle = styled.h2`
  font-size: 1.4rem;
  color: var(--accent-orange);
  font-family: 'Poppins', sans-serif;
  font-weight: 800;
  margin-bottom: 2rem;
  text-align: left;
  position: relative;
  padding-left: 1rem;
  letter-spacing: -0.02em;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 6px;
    height: 100%;
    background: var(--accent-orange);
  }
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.7rem 1rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
  }
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.2rem;

  @media (max-width: 768px) {
    align-items: center;
    width: 100%;
  }
`;

export const Label = styled.label`
  font-size: 1rem;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    font-size: 0.75rem;
    margin-bottom: 0.3rem;
    text-align: center;
    width: 100%;
  }
`;

export const Input = styled.input`
  padding: 0.4rem 0.7rem;
  font-size: 1rem;
  border-radius: 0.5rem;
  border: 1px solid #e0e6ed;
  font-family: 'Poppins', sans-serif;
  background: #fff;
  width: 100%;
  box-sizing: border-box;
  color: var(--text-gray);
  appearance: none;
  -webkit-appearance: none;
  min-height: 2.5rem;

  @media (max-width: 768px) {
    font-size: 16px; /* prevents iOS zoom on focus */
    padding: 0.5rem 0.7rem;
    text-align: center;
    width: 100% !important;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
    appearance: none;
    -webkit-appearance: none;
    color: var(--text-gray);
    -webkit-text-fill-color: var(--text-gray);
    min-height: 2.75rem;
  }
`;

export const Select = styled.select`
  padding: 0.4rem 0.7rem;
  font-size: 1rem;
  border-radius: 0.5rem;
  border: 1px solid #e0e6ed;
  font-family: 'Poppins', sans-serif;
  background: #fff;
  width: 100%;
  box-sizing: border-box;
  color: var(--text-gray);
  appearance: none;
  -webkit-appearance: none;
  min-height: 2.5rem;

  @media (max-width: 768px) {
    font-size: 16px;
    padding: 0.5rem 0.7rem;
    text-align: center;
    text-align-last: center;
    width: 100% !important;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
    appearance: none;
    -webkit-appearance: none;
    color: var(--text-gray);
    -webkit-text-fill-color: var(--text-gray);
    min-height: 2.75rem;
  }
`;

export const SubmitButton = styled.button`
  margin-top: 0.7rem;
  padding: var(--button-padding, 0.4rem 0.75rem);
  border-radius: 8px;
  border: none;
  background: var(--accent-orange);
  color: #fff;
  font-family: 'Poppins', sans-serif;
  font-size: var(--button-font-size, 0.6rem);
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  text-align: center;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  white-space: nowrap;
  outline: none;
  width: 100%;

  &:hover, &:focus {
    background: var(--accent-orange-dark);
    color: #fff;
  }
`;

export const SectionHeader = styled.h2`
  font-size: 1.4rem;
  color: #363636;
  font-family: 'Poppins', sans-serif;
  font-weight: 800;
  margin-bottom: 1.5rem;
  letter-spacing: -0.02em;
`;

export const SessionsList = styled.div`
  background: var(--bg-sidebar);
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.06);
  margin-top: 1rem;

  @media (max-width: 768px) {
    padding: 2rem 0.5rem 2rem 0.5rem;
  }
`;

export const SessionItem = styled.div<{ isPast: boolean }>`
  padding: 1.5rem;
  border-bottom: 1px solid ${props => props.isPast ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.15)'};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 2rem;
  transition: background 0.2s;
  position: relative;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: ${props => props.isPast ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.05)'};
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    padding: 1rem 0.25rem 2rem 0.25rem;
    border-bottom: 2px solid #000;

    &:last-child {
      border-bottom: none;
    }
  }
`;

export const SessionDate = styled.div<{ isPast: boolean }>`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${props => props.isPast ? 'var(--text-muted)' : '#363636'};
  font-family: 'Poppins', sans-serif;
`;

export const SessionType = styled.div<{ isPast: boolean }>`
  font-size: 0.95rem;
  color: ${props => props.isPast ? 'var(--text-muted)' : 'var(--accent-orange)'};
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const SessionLocation = styled.div<{ isPast: boolean }>`
  font-size: 0.95rem;
  color: ${props => props.isPast ? 'var(--text-muted)' : '#363636'};
  font-family: 'Poppins', sans-serif;
  margin-top: 0.25rem;
  margin-bottom: 0.25rem;
`;

export const SessionTime = styled.div<{ isPast: boolean }>`
  font-size: 0.9rem;
  color: ${props => props.isPast ? 'var(--text-muted)' : '#363636'};
  font-family: 'Poppins', sans-serif;
  margin-bottom: 0.5rem;
`;

export const SessionAthletes = styled.div<{ isPast: boolean }>`
  font-size: 0.85rem;
  color: ${props => props.isPast ? 'var(--text-muted)' : '#bbb'};
  font-family: 'Poppins', sans-serif;
  margin-top: 0.5rem;
`;

export const StartButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  border: 2px solid var(--accent-orange);
  background: var(--accent-orange);
  color: #fff;
  font-family: 'Poppins', sans-serif;
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: -0.02em;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
  outline: none;

  &:hover, &:focus {
    background: var(--accent-orange-dark);
    border-color: var(--accent-orange-dark);
    color: #fff;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const ActionButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

export const IconButton = styled.button`
  padding: 0.5rem;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: #333;
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const EditButton = styled(IconButton)`
  &:hover {
    background: rgba(33, 150, 243, 0.1);
    color: #2196F3;
  }
`;

export const DeleteButton = styled(IconButton)`
  &:hover {
    background: rgba(244, 67, 54, 0.1);
    color: #f44336;
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #888;
  font-family: 'Poppins', sans-serif;
`; 